/**
 * CreateMasterModal — Modale de création unifiée Master + membres + contact commun.
 *
 * POST /api/masters/with-members
 *
 * Extrait de MasterClientsList.jsx (Batch #14.221 — refactor oversized 2026-02).
 */
import { useState } from "react";
import axios from "axios";
import { X, Loader2, Users, FolderTree, FileText, Star } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


function Section({ title, icon: Icon, tone = "slate", children }) {
  const tones = {
    indigo: "border-indigo-200 bg-indigo-50/30",
    purple: "border-purple-200 bg-purple-50/30",
    emerald: "border-emerald-200 bg-emerald-50/30",
    slate: "border-slate-200 bg-slate-50/40",
  };
  return (
    <div className={`border rounded-xl p-4 mb-3 ${tones[tone]}`}>
      <h4 className="font-semibold text-slate-700 inline-flex items-center gap-1.5 text-sm mb-2">
        <Icon className="w-4 h-4 text-slate-500" /> {title}
      </h4>
      {children}
    </div>
  );
}

function Field({ label, hint, wide = false, children }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-xs text-slate-500 font-medium">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-slate-400 mt-0.5 block italic">{hint}</span>}
    </label>
  );
}

export default function CreateMasterModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    primary: { first_name: "", last_name: "", email: "", phone: "" },
    secondary: { first_name: "", last_name: "", email: "", phone: "" },
    has_secondary: false,
    couple_display_name: "",
    common_contact: {
      civic_number: "", street: "", apartment: "",
      city: "", province: "QC", postal_code: "",
      home_phone: "", preferred_contact_method: "email",
    },
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const updP = (k, v) => setForm((f) => ({ ...f, primary: { ...f.primary, [k]: v } }));
  const updS = (k, v) => setForm((f) => ({ ...f, secondary: { ...f.secondary, [k]: v } }));
  const updCC = (k, v) => setForm((f) => ({ ...f, common_contact: { ...f.common_contact, [k]: v } }));

  const submit = async () => {
    if (!form.primary.first_name.trim() || !form.primary.last_name.trim()) {
      toast.error("Prénom et nom du Principal sont obligatoires");
      return;
    }
    if (form.has_secondary && (!form.secondary.first_name.trim() || !form.secondary.last_name.trim())) {
      toast.error("Prénom et nom du Secondaire sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      const body = {
        primary: { ...form.primary },
        common_contact: { ...form.common_contact },
      };
      if (form.has_secondary) body.secondary = { ...form.secondary };
      if (form.couple_display_name.trim()) body.couple_display_name = form.couple_display_name.trim();
      if (form.notes.trim()) body.notes = form.notes.trim();
      const r = await axios.post(`${API}/masters/with-members`, body, { headers: authH() });
      toast.success("Dossier Master créé avec succès");
      onCreated(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-lg inline-flex items-center gap-2">
              <Star className="w-5 h-5 text-indigo-600" /> Nouveau dossier Master
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Création unifiée : Principal{form.has_secondary ? " + Secondaire" : ""} + Master en une seule étape
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="masters-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* PRINCIPAL */}
        <Section title="Compte principal" icon={Users} tone="indigo">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field label="Prénom *">
              <input className="input" value={form.primary.first_name} onChange={(e) => updP("first_name", e.target.value)} data-testid="master-primary-first-name" />
            </Field>
            <Field label="Nom *">
              <input className="input" value={form.primary.last_name} onChange={(e) => updP("last_name", e.target.value)} data-testid="master-primary-last-name" />
            </Field>
            <Field label="Courriel (individuel)" hint="Identifiant portail">
              <input type="email" className="input" value={form.primary.email} onChange={(e) => updP("email", e.target.value)} data-testid="master-primary-email" />
            </Field>
            <Field label="Mobile (individuel)" hint="Pour SMS et signatures électroniques">
              <input className="input" value={form.primary.phone} onChange={(e) => updP("phone", e.target.value)} data-testid="master-primary-phone" />
            </Field>
          </div>
        </Section>

        {/* TOGGLE SECONDAIRE */}
        <div className="my-3">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.has_secondary}
              onChange={(e) => setForm((f) => ({ ...f, has_secondary: e.target.checked }))}
              className="rounded text-indigo-600"
              data-testid="master-toggle-secondary"
            />
            <span className="font-medium text-slate-700">Ajouter un compte secondaire (conjoint·e)</span>
          </label>
        </div>

        {/* SECONDAIRE */}
        {form.has_secondary && (
          <Section title="Compte secondaire" icon={Users} tone="purple">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Field label="Prénom *">
                <input className="input" value={form.secondary.first_name} onChange={(e) => updS("first_name", e.target.value)} data-testid="master-secondary-first-name" />
              </Field>
              <Field label="Nom *">
                <input className="input" value={form.secondary.last_name} onChange={(e) => updS("last_name", e.target.value)} data-testid="master-secondary-last-name" />
              </Field>
              <Field label="Courriel (individuel)" hint="Identifiant portail distinct">
                <input type="email" className="input" value={form.secondary.email} onChange={(e) => updS("email", e.target.value)} />
              </Field>
              <Field label="Mobile (individuel)">
                <input className="input" value={form.secondary.phone} onChange={(e) => updS("phone", e.target.value)} />
              </Field>
            </div>
          </Section>
        )}

        {/* COMMUN */}
        <Section title="Coordonnées communes (couple)" icon={FolderTree} tone="emerald">
          <p className="text-[11px] text-slate-500 mb-3">
            Adresse partagée et téléphone maison. Le courriel et le mobile restent individuels (sections ci-dessus).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <Field label="N° civique">
              <input className="input" value={form.common_contact.civic_number} onChange={(e) => updCC("civic_number", e.target.value)} data-testid="master-cc-civic" />
            </Field>
            <Field label="Rue" wide>
              <input className="input" value={form.common_contact.street} onChange={(e) => updCC("street", e.target.value)} data-testid="master-cc-street" />
            </Field>
            <Field label="Appartement">
              <input className="input" value={form.common_contact.apartment} onChange={(e) => updCC("apartment", e.target.value)} />
            </Field>
            <Field label="Ville">
              <input className="input" value={form.common_contact.city} onChange={(e) => updCC("city", e.target.value)} data-testid="master-cc-city" />
            </Field>
            <Field label="Province">
              <input className="input" value={form.common_contact.province} onChange={(e) => updCC("province", e.target.value)} maxLength={2} />
            </Field>
            <Field label="Code postal">
              <input className="input" value={form.common_contact.postal_code} onChange={(e) => updCC("postal_code", e.target.value)} data-testid="master-cc-postal" />
            </Field>
            <Field label="Téléphone maison">
              <input className="input" value={form.common_contact.home_phone} onChange={(e) => updCC("home_phone", e.target.value)} />
            </Field>
            <Field label="Mode de contact privilégié">
              <select className="input" value={form.common_contact.preferred_contact_method} onChange={(e) => updCC("preferred_contact_method", e.target.value)} data-testid="master-cc-pref-contact">
                <option value="email">Courriel</option>
                <option value="phone">Téléphone</option>
                <option value="sms">SMS</option>
              </select>
            </Field>
          </div>
        </Section>

        {/* META */}
        <Section title="Informations dossier" icon={FileText} tone="slate">
          <div className="space-y-3 text-sm">
            <Field label="Nom du couple/famille" hint="Auto-calculé si vide (ex: Tremblay/Gagnon)">
              <input className="input" value={form.couple_display_name} onChange={(e) => setForm((f) => ({ ...f, couple_display_name: e.target.value }))} data-testid="master-couple-name" />
            </Field>
            <Field label="Notes internes">
              <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </Field>
          </div>
        </Section>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border" data-testid="master-create-cancel">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="masters-create-save"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Créer le Master
          </button>
        </div>
      </div>
      <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #6366f1; box-shadow: 0 0 0 1px #6366f1; }`}</style>
    </div>
  );
}
